"use client";

/**
 * Marketing-site theme provider + toggle.
 *
 * We deliberately use `next-themes` directly here instead of the shared
 * `@askfinz/shared-components` ThemeProvider. That shared provider runs a
 * `ThemeWatcher` that re-syncs the theme from a cross-subdomain cookie / DB
 * preferences on every change — and for logged-out visitors (the marketing
 * audience) its "cookie, no DB prefs" branch never locks its synced flag, so
 * it keeps reverting the toggle. The marketing site has no logged-in prefs to
 * sync, so a plain next-themes provider is correct and reliable.
 */

import { useEffect, useState } from "react";
import { ThemeProvider as NextThemesProvider, useTheme } from "next-themes";
import { Moon, Sun } from "lucide-react";

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      storageKey="AskFinz_theme"
      disableTransitionOnChange
    >
      {children}
    </NextThemesProvider>
  );
}

export function ThemeToggle({ className }: { className?: string }) {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // Avoid a hydration mismatch: the server can't know the resolved theme, so
  // render a fixed placeholder until mounted.
  useEffect(() => setMounted(true), []);

  const base =
    "inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-foreground/5 transition-colors";

  if (!mounted) {
    return <span aria-hidden className={(className ? className + " " : "") + base} />;
  }

  const isDark = resolvedTheme === "dark";
  return (
    <button
      type="button"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
      className={(className ? className + " " : "") + base}
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      <span className="sr-only">Toggle theme</span>
    </button>
  );
}
